/**
 * Create/edit drawer for a dated operating exception.
 *
 * CUSTOM_HOURS reveals open/close time fields (required + validated as
 * open < close, mirroring the backend `clean()`); CLOSED / OPEN hide them.
 */

import { useEffect } from 'react';
import { Field, SimpleGrid, VStack } from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DrawerComponent,
  InputField,
  SelectInput,
} from '@themes/aegix';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  operatingExceptionFormSchema,
  type OperatingExceptionFormValues,
} from '../../../validations/whakahaereConfig.validation';
import {
  EXCEPTION_KIND_LABELS,
  type OperatingException,
  type ExceptionKind,
} from '../../../services';
import {
  useCreateOperatingException,
  useUpdateOperatingException,
} from '../../../hooks/queries';
import { toaster } from '../../ui/toaster';

interface OperatingExceptionDrawerProps {
  calendarId: number;
  isOpen: boolean;
  row: OperatingException | null;
  onClose: () => void;
  onDelete: (id: number) => void;
}

const KIND_OPTIONS = (
  Object.keys(EXCEPTION_KIND_LABELS) as ExceptionKind[]
).map((k) => ({ value: k, label: EXCEPTION_KIND_LABELS[k] }));

function defaults(row: OperatingException | null): OperatingExceptionFormValues {
  return {
    date: row?.date ?? '',
    kind: row?.kind ?? 'closed',
    open_time: row?.open_time ? row.open_time.slice(0, 5) : '',
    close_time: row?.close_time ? row.close_time.slice(0, 5) : '',
    reason: row?.reason ?? '',
  };
}

export function OperatingExceptionDrawer({
  calendarId,
  isOpen,
  row,
  onClose,
  onDelete,
}: OperatingExceptionDrawerProps) {
  const createException = useCreateOperatingException();
  const updateException = useUpdateOperatingException(calendarId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OperatingExceptionFormValues>({
    defaultValues: defaults(row),
    resolver: zodResolver(operatingExceptionFormSchema),
    mode: 'onBlur',
  });

  const kind = useWatch({ control, name: 'kind' });

  useEffect(() => {
    if (isOpen) reset(defaults(row));
  }, [isOpen, row, reset]);

  const onSubmit = async (values: OperatingExceptionFormValues) => {
    // Only custom_hours carries times; clear them otherwise so the backend
    // doesn't store stale open/close for a CLOSED/OPEN day.
    const isCustom = values.kind === 'custom_hours';
    const payload = {
      calendar: calendarId,
      date: values.date,
      kind: values.kind as ExceptionKind,
      open_time: isCustom ? values.open_time || null : null,
      close_time: isCustom ? values.close_time || null : null,
      reason: values.reason ?? '',
    };
    try {
      if (row) {
        await updateException.mutateAsync({ id: row.id, data: payload });
        toaster.create({ title: 'Exception updated', type: 'success' });
      } else {
        await createException.mutateAsync(payload);
        toaster.create({ title: 'Exception added', type: 'success' });
      }
      onClose();
    } catch (err) {
      toaster.create({
        title: 'Could not save the exception',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={row ? 'Edit exception' : 'Add exception'}
      size="sm"
      closeOnOutside={false}
      footer={
        <SimpleGrid columns={row ? 3 : 2} gap={3} width="100%">
          {row && (
            <ButtonComponent
              visual="alert"
              type="button"
              onClick={() => onDelete(row.id)}
              data-e2e="delete-exception-button"
            >
              Delete
            </ButtonComponent>
          )}
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-exception-button"
          >
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="submit"
            form="operating-exception-form"
            disabled={isSubmitting}
            data-e2e="save-exception-button"
          >
            Save
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <form id="operating-exception-form" onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={4} align="stretch">
          <Field.Root invalid={!!errors.date}>
            <Field.Label htmlFor="exception-date">Date</Field.Label>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <InputField
                  id="exception-date"
                  type="date"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  data-e2e="exception-date-input"
                />
              )}
            />
            {errors.date && (
              <Field.ErrorText>{errors.date.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.kind}>
            <Field.Label htmlFor="exception-kind">Kind</Field.Label>
            <Controller
              name="kind"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="exception-kind"
                  value={field.value}
                  option={KIND_OPTIONS}
                  onChange={(val: string) => field.onChange(val)}
                  searchable={false}
                  e2eTitle="exception-kind-select"
                />
              )}
            />
            {errors.kind && (
              <Field.ErrorText>{errors.kind.message}</Field.ErrorText>
            )}
          </Field.Root>

          {kind === 'custom_hours' && (
            <>
              <Field.Root invalid={!!errors.open_time}>
                <Field.Label htmlFor="exception-open">Opens</Field.Label>
                <Controller
                  name="open_time"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      id="exception-open"
                      type="time"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-e2e="exception-open-input"
                    />
                  )}
                />
                {errors.open_time && (
                  <Field.ErrorText>{errors.open_time.message}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.close_time}>
                <Field.Label htmlFor="exception-close">Closes</Field.Label>
                <Controller
                  name="close_time"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      id="exception-close"
                      type="time"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-e2e="exception-close-input"
                    />
                  )}
                />
                {errors.close_time && (
                  <Field.ErrorText>{errors.close_time.message}</Field.ErrorText>
                )}
              </Field.Root>
            </>
          )}

          <Field.Root invalid={!!errors.reason}>
            <Field.Label htmlFor="exception-reason">Reason</Field.Label>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <InputField
                  id="exception-reason"
                  placeholder="e.g. Thanksgiving break, Early release"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  data-e2e="exception-reason-input"
                />
              )}
            />
            {errors.reason && (
              <Field.ErrorText>{errors.reason.message}</Field.ErrorText>
            )}
          </Field.Root>
        </VStack>
      </form>
    </DrawerComponent>
  );
}
